import aygLogo from "@assets/images/brand/jcp-icon.png";
import fallbackImage from "@assets/images/fallback-game.svg";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {SocialEntrySelector} from "@/components/SocialEntrySelector";
import {useGetBigWinners} from "@/hooks/gamesList/useGetBigWinners";
import {useProviderTopGames} from "@/hooks/gamesList/useProviderTopGames";
import {useAuthStore} from "@/store/useAuth.store";
import {useMiniGames} from "@/store/useMiniGames";
import useStories from "../hooks/useStories";
import StoryGate from "../StoryGate/StoryGate";
import StoryItem from "../StoryItem/StoryItem";
import AYGStoryViewer from "../StoryViewers/AYGStoryViewer";
import styles from "./StoriesCarousel.module.css";

const PLACEHOLDER_TEXT = "Contenido a definir";

const makePlaceholderStories = label => [
  {id: `${label}-1`, name: `${PLACEHOLDER_TEXT} — ${label} (1/3)`, type: "placeholder"},
  {id: `${label}-2`, name: `${PLACEHOLDER_TEXT} — ${label} (2/3)`, type: "placeholder"},
  {id: `${label}-3`, name: `${PLACEHOLDER_TEXT} — ${label} (3/3)`, type: "placeholder"},
];

/**
 * @typedef {Object} StoryGroup
 * @property {string} circleId - ID of the story circle that owns this group
 * @property {string} avatar - Avatar image for the viewer header
 * @property {string} displayName - Name shown in viewer header
 * @property {string|null} profilePath - Navigation path for avatar tap
 * @property {string} [providerKey] - Provider slug when this group is a provider story
 * @property {Array<{id: string, name: string, src?: string, type: string}>} viewerStories - Stories for the viewer
 */

const StoriesCarousel = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const authStatus = useAuthStore(state => state.authStatus);
  const getMiniGames = useMiniGames(state => state.getMiniGames);
  const fullGameList = useMiniGames(state => state.list);
  const favoriteGames = useMiniGames(state => state.favoriteGames);
  const providerOptions = useMiniGames(state => state.providerOptions);
  const {winners} = useGetBigWinners();

  const [viewedIds, setViewedIds] = useState(new Set());
  const [activeGroupIndex, setActiveGroupIndex] = useState(null);
  const [selectorStory, setSelectorStory] = useState(null);
  const selectorAnchorRef = useRef(null);

  const loading = useMiniGames(state => state.loading);
  const hasNoFavorites = authStatus === "authenticated" && !loading && favoriteGames.length === 0;

  const {stories, showGate, userInitials, userData, avatarImage} = useStories(viewedIds);
  const {topGamesByProvider, requestProviders} = useProviderTopGames();

  useEffect(() => {
    if (providerOptions.length === 0 && fullGameList.length === 0 && !loading) {
      getMiniGames("all", "");
    }
  }, [providerOptions.length, fullGameList.length, getMiniGames, loading]);

  const markViewed = useCallback(id => {
    setViewedIds(prev => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  /** Convert a winner entry to a bigwin story slide */
  const toBigWinStory = useCallback(
    (winner, index, prefix) => ({
      id: `${prefix}-bigwin-${index}`,
      name: `@${winner.user_alias}`,
      src: winner.icon || fallbackImage,
      mediaSrc: winner.video_url || null,
      type: winner.video_url ? "video" : "bigwin",
      winnerAlias: winner.user_alias,
      winnerAmount: winner.amount_float,
      winnerCurrency: winner.currency,
      winnerGame: winner.game_name,
      gameId: winner.game_id || null,
      gameProvider: winner.provider || null,
    }),
    []
  );

  /** Build the unified story queue: one group per story circle, in display order */
  const storyQueue = useMemo(() => {
    /** @type {StoryGroup[]} */
    const queue = [];
    const visualizableFavorites = favoriteGames
      .map(fav => {
        const matchedGame = fullGameList.find(
          game => String(game.id) === String(fav.game_id) && String(game.provider) === String(fav.game_provider)
        );
        const resolvedId = fav.game_id || matchedGame?.id;
        const resolvedProvider = fav.game_provider || matchedGame?.provider;
        const resolvedName = matchedGame?.name || fav.name || fav.game_name || "";
        const resolvedIcon = matchedGame?.icon || fav.icon || fallbackImage;

        return {
          ...fav,
          game_id: resolvedId,
          game_provider: resolvedProvider,
          name: resolvedName,
          icon: resolvedIcon,
        };
      })
      .filter(fav => !!fav.game_id && !!fav.game_provider && !!fav.name);
    const favoriteTop3 = visualizableFavorites.slice(0, 3);

    for (const story of stories) {
      if (story.type === "user") {
        const userWins = winners.filter(w => w.user_alias === userData?.alias);
        queue.push({
          circleId: story.id,
          avatar: avatarImage || "",
          displayName: story.label,
          profilePath: `/u/${userData?.alias || "me"}`,
          viewerStories:
            userWins.length > 0
              ? userWins.map((w, i) => toBigWinStory(w, i, "user"))
              : favoriteTop3.length > 0
                ? favoriteTop3.map((game, i) => ({
                    id: `user-favorite-${game.game_id}-${game.game_provider}-${i}`,
                    name: game.name || `Juego favorito #${i + 1}`,
                    src: game.icon || fallbackImage,
                    type: "topgame",
                    rank: i + 1,
                    gameName: game.name || `Juego favorito #${i + 1}`,
                    gameId: game.game_id,
                    gameProvider: game.game_provider,
                    providerTitle: "Tus juegos favoritos",
                  }))
                : makePlaceholderStories(hasNoFavorites ? "Sin favoritos" : "Perfil"),
        });
      } else if (story.type === "community") {
        queue.push({
          circleId: story.id,
          avatar: "",
          displayName: "Comunidad",
          profilePath: null,
          viewerStories:
            winners.length > 0
              ? winners.map((w, i) => toBigWinStory(w, i, "community"))
              : makePlaceholderStories("Comunidad"),
        });
      } else if (story.type === "promotion") {
        queue.push({
          circleId: story.id,
          avatar: aygLogo,
          displayName: story.label,
          profilePath: "/provider/ayg",
          viewerStories: [
            {
              id: story.id,
              name: story.label,
              src: story.icon,
              type: "image",
            },
          ],
        });
      } else if (story.type === "provider" && story.providerKey) {
        const top3 = topGamesByProvider[story.providerKey] || [];
        queue.push({
          circleId: story.id,
          avatar: story.icon,
          displayName: story.label,
          profilePath: `/provider/${story.providerKey}`,
          providerKey: story.providerKey,
          viewerStories:
            top3.length > 0
              ? top3.map((game, i) => ({
                  id: `${story.providerKey}-top-${i}`,
                  name: game.name,
                  src: game.thumbnail_url || game.icon || fallbackImage,
                  type: "topgame",
                  rank: i + 1,
                  gameName: game.name,
                  gameId: game.id,
                  gameProvider: game.provider,
                  providerTitle: t("storyTopGamesTitle", {provider: story.label}),
                }))
              : makePlaceholderStories(story.providerKey),
        });
      }
    }

    return queue;
  }, [
    stories,
    avatarImage,
    userData,
    winners,
    toBigWinStory,
    topGamesByProvider,
    favoriteGames,
    fullGameList,
    hasNoFavorites,
    t,
  ]);

  /** Prefetch top games for the active provider story and the next one in queue */
  useEffect(() => {
    if (activeGroupIndex === null) return;

    const keys = [];
    const current = storyQueue[activeGroupIndex];
    const next = storyQueue[activeGroupIndex + 1];
    if (current?.providerKey) keys.push(current.providerKey);
    if (next?.providerKey) keys.push(next.providerKey);
    if (keys.length > 0) requestProviders(keys);
  }, [activeGroupIndex, storyQueue, requestProviders]);

  /** Current active group from the queue */
  const activeGroup = activeGroupIndex !== null ? storyQueue[activeGroupIndex] : null;

  /** Advance to the next group in queue, or close if at end */
  const handleGroupComplete = useCallback(() => {
    setActiveGroupIndex(prev => {
      if (prev === null) return null;
      const next = prev + 1;
      if (next < storyQueue.length) {
        return next;
      }
      return null;
    });
  }, [storyQueue.length]);

  /** Go back to the previous group in queue */
  const handleGroupPrev = useCallback(() => {
    setActiveGroupIndex(prev => {
      if (prev === null || prev <= 0) return prev;
      return prev - 1;
    });
  }, []);

  const handleViewerClose = useCallback(() => {
    setActiveGroupIndex(null);
  }, []);

  // ═══ Open the story viewer starting from a specific circle ═══
  const openStoryViewer = useCallback(
    story => {
      const idx = storyQueue.findIndex(g => g.circleId === story.id);
      if (idx >= 0) {
        setActiveGroupIndex(idx);
        markViewed(story.id);
      }
    },
    [storyQueue, markViewed]
  );

  /** Mark viewed when group changes */
  useEffect(() => {
    if (activeGroup) {
      markViewed(activeGroup.circleId);
    }
  }, [activeGroup, markViewed]);

  const getEntityType = useCallback(story => {
    if (story.type === "promotion") return "ayg";
    if (story.type === "provider") return "provider";
    if (story.type === "community") return "community";
    return "user";
  }, []);

  const needsSelector = useCallback(story => {
    if (story.type === "promotion") return true;
    if (story.type === "provider") return true;
    if (story.type === "community") return true;
    if (story.type === "user") return true;
    return false;
  }, []);

  const handleStoryOpen = useCallback(
    (story, anchorElement) => {
      if (authStatus !== "authenticated") {
        useAuthStore.getState().handleAuthForm("registration");
        useAuthStore.getState().handleShowAuthModal();
        return;
      }
      if (story.type === "provider" && story.providerKey) {
        requestProviders([story.providerKey]);
      }
      if (needsSelector(story)) {
        setSelectorStory(story);
        selectorAnchorRef.current = anchorElement || null;
      } else {
        openStoryViewer(story);
      }
    },
    [authStatus, needsSelector, openStoryViewer, requestProviders]
  );

  const navigateToProfile = useCallback(
    story => {
      if (authStatus !== "authenticated") {
        useAuthStore.getState().handleAuthForm("registration");
        useAuthStore.getState().handleShowAuthModal();
        return;
      }
      if (story.type === "promotion") {
        navigate("/provider/ayg");
        return;
      }
      if (story.type === "provider" && story.providerKey) {
        navigate(`/provider/${story.providerKey}`);
        return;
      }
      if (story.type === "user") {
        navigate(`/u/${userData?.alias || "me"}`);
        return;
      }
    },
    [authStatus, navigate, userData]
  );

  const handleSelectorViewStories = useCallback(() => {
    if (selectorStory) openStoryViewer(selectorStory);
  }, [selectorStory, openStoryViewer]);

  const handleSelectorGoToProfile = useCallback(() => {
    if (selectorStory) navigateToProfile(selectorStory);
  }, [selectorStory, navigateToProfile]);

  const handleSelectorGoToCommunity = useCallback(() => {
    if (selectorStory) {
      openStoryViewer(selectorStory);
    }
  }, [selectorStory, openStoryViewer]);

  const handleSelectorClose = useCallback(() => {
    setSelectorStory(null);
  }, []);

  const handleSelectorPlayInFeed = useCallback(() => {
    if (selectorStory?.providerKey) {
      navigate(`/game-feed?provider=${selectorStory.providerKey}`);
    } else {
      navigate("/game-feed");
    }
  }, [selectorStory, navigate]);

  const selectorData = useMemo(() => {
    if (!selectorStory) return null;
    const entityType = getEntityType(selectorStory);
    return {
      entityType,
      displayName: selectorStory.label,
      avatar: selectorStory.icon || "",
      initials: selectorStory.type === "user" ? userInitials : "",
      hasStories: true,
    };
  }, [selectorStory, getEntityType, userInitials]);

  const {promoStories, providerStories, metaStories} = useMemo(() => {
    const meta = stories.filter(s => s.type === "user" || s.type === "community");
    const promo = stories.filter(s => s.type === "promotion");
    const provider = stories.filter(s => s.type === "provider");
    return {metaStories: meta, promoStories: promo, providerStories: provider};
  }, [stories]);

  return (
    <section
      className={styles.stories}
      aria-label="Historias"
    >
      <div className={styles.stories__row}>
        {metaStories.map(story => (
          <StoryItem
            key={story.id}
            story={story}
            onOpen={handleStoryOpen}
            userInitials={userInitials}
          />
        ))}

        {metaStories.length > 0 && promoStories.length > 0 && <div className={styles.stories__divider} />}

        {promoStories.map(story => (
          <StoryItem
            key={story.id}
            story={story}
            onOpen={handleStoryOpen}
          />
        ))}

        {showGate && <StoryGate />}

        {providerStories.length > 0 && <div className={styles.stories__divider} />}

        {providerStories.map(story => (
          <StoryItem
            key={story.id}
            story={story}
            onOpen={handleStoryOpen}
          />
        ))}
      </div>

      {/* ── Unified Story Viewer ── */}
      {activeGroup && (
        <AYGStoryViewer
          key={activeGroup.circleId}
          stories={activeGroup.viewerStories}
          initialIndex={0}
          onClose={handleViewerClose}
          onComplete={handleGroupComplete}
          onGroupPrev={activeGroupIndex > 0 ? handleGroupPrev : undefined}
          onViewed={markViewed}
          avatar={activeGroup.avatar}
          displayName={activeGroup.displayName}
          profilePath={activeGroup.profilePath}
        />
      )}

      {/* ── Social Entry Selector ── */}
      {selectorData && (
        <SocialEntrySelector
          isOpen={selectorStory !== null}
          entityType={selectorData.entityType}
          displayName={selectorData.displayName}
          avatar={selectorData.avatar}
          initials={selectorData.initials}
          hasStories={selectorData.hasStories}
          onViewStories={handleSelectorViewStories}
          onGoToProfile={selectorData.entityType !== "community" ? handleSelectorGoToProfile : undefined}
          onGoToCommunity={selectorData.entityType === "community" ? handleSelectorGoToCommunity : undefined}
          onPlayInFeed={selectorData.entityType === "provider" ? handleSelectorPlayInFeed : undefined}
          onClose={handleSelectorClose}
          anchorRef={selectorAnchorRef}
        />
      )}
    </section>
  );
};

export default StoriesCarousel;
